import { createRouter, createWebHistory } from 'vue-router'
import { $t } from '@previo/core'
import { CrmModule } from '~/composables/usePermissionChecker'
import { Layouts } from '~/layouts'
import auth from '~/router/middleware/auth'
import loadUser from '~/router/middleware/loadUser'
import moduleGuard from '~/router/middleware/moduleGuard'
import leadToSalesCase from '~/router/middleware/leadToSalesCase'
import entryPoint from '~/router/middleware/entryPoint'
import P1routes from '~/router/P1routes'
import { isChunkLoadError, recoverFromChunkLoadError } from '~/plugins/chunkLoadRecovery'
import { Routes } from './routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: Routes.LOGIN_INDEX,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Login')) },
      component: () => import('~/pages/login/Index.vue'),
    },
    {
      path: '',
      meta: {
        layout: Layouts.AUTH,
      },
      beforeEnter: [auth, loadUser],
      children: [
        {
          path: '/',
          name: Routes.HOME_INDEX,
          beforeEnter: [entryPoint],
          children: [],
        },
        {
          path: '/sale/leads',
          redirect: { name: Routes.SALES_CASES_INDEX },
        },
        {
          path: '/sale/sales-cases',
          name: Routes.SALES_CASES_INDEX,
          redirect: { name: Routes.SALES_CASES_TABLE },
          meta: {
            requiredCrmModule: CrmModule.SALES_CASES,
          },
          component: () => import('~/pages/sales/sales-cases/Index.vue'),
          children: [
            {
              path: 'table',
              name: Routes.SALES_CASES_TABLE,
              meta: {
                title: () => pageTitle($t('Sales cases table')),
                requiredCrmModule: CrmModule.SALES_CASES,
              },
              component: () => import('~/pages/sales/sales-cases/Table.vue'),
            },
            {
              path: 'kanban',
              name: Routes.SALES_CASES_KANBAN,
              meta: {
                title: () => pageTitle($t('Sales cases kanban')),
                requiredCrmModule: CrmModule.SALES_CASES,
              },
              component: () => import('~/pages/sales/sales-cases/Kanban.vue'),
            },
            {
              path: 'map',
              name: Routes.SALES_CASES_MAP,
              meta: {
                title: () => pageTitle($t('Sales cases map')),
                requiredCrmModule: CrmModule.SALES_CASES,
              },
              component: () => import('~/pages/sales/sales-cases/Map.vue'),
            },
            {
              path: 'summary',
              name: Routes.SALES_CASES_SUMMARY,
              meta: {
                title: () => pageTitle($t('Sales cases summary')),
                requiredCrmModule: CrmModule.SALES_CASES,
              },
              component: () => import('~/pages/sales/sales-cases/Summary.vue'),
            },
          ],
        },
        {
          path: '/sale/sales-cases/:salesCaseId',
          name: Routes.SALES_CASE_DETAIL,
          component: () => import('~/pages/sales/sales-cases/Detail.vue'),
          meta: {
            title: () => pageTitle($t('Sales case detail')),
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredCrmModule: CrmModule.SALES_CASES,
          },
        },
        {
          path: '/sale/dashboard',
          name: Routes.SALES_DASHBOARD_INDEX,
          meta: {
            title: () => pageTitle($t('Sales dashboard')),
            requiredCrmModule: CrmModule.SALES_DASHBOARD,
          },
          component: () => import('~/pages/sales/dashboard/Index.vue'),
        },
        {
          path: '/sale/contracts',
          name: Routes.SALES_CONTRACTS_INDEX,
          meta: {
            title: () => pageTitle($t('Contracts')),
            requiredCrmModule: CrmModule.SALES_DOCUMENTS,
          },
          component: () => import('~/pages/sales/contracts/Index.vue'),
        },
        {
          path: '/sale/reference-sets',
          name: Routes.SALES_REFERENCE_SETS_INDEX,
          meta: {
            title: () => pageTitle($t('Reference sets')),
            requiredCrmModule: CrmModule.SALES_SALES,
          },
          component: () => import('~/pages/sales/reference-sets/Index.vue'),
        },
        {
          path: '/sale/reference-sets/:referenceSetId',
          name: Routes.SALES_REFERENCE_SETS_DETAIL,
          meta: {
            title: () => pageTitle($t('Reference set detail')),
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredCrmModule: CrmModule.SALES_SALES,
          },
          component: () => import('~/pages/sales/reference-sets/Detail.vue'),
        },
        {
          path: '/sale/salesmen',
          name: Routes.SALES_SALESMEN_INDEX,
          meta: {
            title: () => pageTitle($t('Salesmen')),
            requiredCrmModule: CrmModule.SALESMEN,
          },
          component: () => import('~/pages/sales/salesmen/Index.vue'),
        },
        {
          path: '/sale/leads/summary',
          redirect: { name: Routes.SALES_CASES_INDEX },
        },
        {
          path: '/sale/leads/:leadId',
          name: Routes.SALES_LEADS_DETAIL,
          beforeEnter: [leadToSalesCase],
          meta: {
            requiredCrmModule: CrmModule.SALES_CASES,
          },
          children: [],
        },
        {
          path: '/backoffice/contact-persons',
          name: Routes.BACKOFFICE_CONTACT_PERSON_INDEX,
          meta: {
            title: () => pageTitle($t('Contact persons')),
            requiredCrmModule: CrmModule.CONTACT_PERSONS,
          },
          component: () => import('~/pages/backoffice/contact-person/Index.vue'),
        },
        {
          path: '/users/employees',
          name: Routes.USERS_EMPLOYEES,
          meta: {
            title: () => pageTitle($t('Employees')),
            requiredCrmModule: CrmModule.EMPLOYEES,
          },
          component: () => import('~/pages/users/employees/Index.vue'),
        },
        {
          path: '/users/api-users',
          name: Routes.USERS_API_USERS,
          meta: {
            title: () => pageTitle($t('API users')),
            requiredCrmModule: CrmModule.API_USERS,
          },
          component: () => import('~/pages/users/api-users/Index.vue'),
        },
        {
          path: '/users/api-users/integration-partners',
          name: Routes.USERS_API_USERS_INTEGRATION_PARTNERS,
          meta: {
            title: () => pageTitle($t('Integration partners')),
            requiredCrmModule: CrmModule.INTEGRATION_PARTNERS,
          },
          component: () => import('~/pages/users/api-users/integration-partners/Index.vue'),
        },
        {
          path: '/users/groups',
          name: Routes.USERS_GROUPS,
          meta: {
            title: () => pageTitle($t('Groups')),
            requiredCrmModule: CrmModule.USERS_GROUPS,
          },
          component: () => import('~/pages/users/groups/Index.vue'),
        },
        {
          path: '/system/ntak-version',
          name: Routes.SYSTEM_NTAK_VERSION,
          meta: {
            title: () => pageTitle($t('NTAK version')),
          },
          component: () => import('~/pages/system/ntak-version/Index.vue'),
        },
        {
          path: '/users/activity',
          name: Routes.USERS_ACTIVITY,
          meta: {
            title: () => pageTitle($t('Users activity')),
            requiredCrmModule: CrmModule.USERS_ACTIVITY,
          },
          component: () => import('~/pages/users/activity/Index.vue'),
        },
        {
          path: '/ota/dev-tools/chm-connections',
          name: Routes.OTA_DEV_TOOLS_CHM_CONNECTIONS,
          meta: {
            title: () => pageTitle($t('CHM Connections')),
            requiredCrmModule: CrmModule.OTA_CM,
          },
          component: () => import('~/pages/ota/dev-tools/chm-connections/Index.vue'),
        },
        {
          path: '/ota/dev-tools/reservation-room-lookup',
          name: Routes.OTA_DEV_TOOLS_RESERVATION_ROOM_LOOKUP,
          meta: {
            title: () => pageTitle($t('Reservation room lookup')),
            requiredCrmModule: CrmModule.OTA_CM,
          },
          component: () => import('~/pages/ota/dev-tools/reservation-room-lookup/Index.vue'),
        },
        {
          path: '/ota/dev-tools/ical-viewer',
          name: Routes.OTA_DEV_TOOLS_ICAL_VIEWER,
          meta: {
            title: () => pageTitle($t('iCal viewer')),
            requiredCrmModule: CrmModule.OTA_CM,
          },
          component: () => import('~/pages/ota/dev-tools/ical-viewer/Index.vue'),
        },
        {
          path: '/system/pms-version-overrides',
          name: Routes.SYSTEM_PMS_VERSION_OVERRIDES,
          redirect: { name: Routes.SYSTEM_PMS_VERSION_OVERRIDES_TABLE },
          meta: {
            requiredCrmModule: CrmModule.SYSTEM_PMS_VERSION_OVERRIDES,
          },
          component: () => import('~/pages/system/pms-version-overrides/Index.vue'),
          children: [
            {
              path: 'table',
              name: Routes.SYSTEM_PMS_VERSION_OVERRIDES_TABLE,
              meta: {
                title: () => pageTitle($t('PMS version')),
                requiredCrmModule: CrmModule.SYSTEM_PMS_VERSION_OVERRIDES,
              },
              component: () => import('~/pages/system/pms-version-overrides/pages/table/Index.vue'),
            },
            {
              path: 'statistics',
              name: Routes.SYSTEM_PMS_VERSION_OVERRIDES_STATISTICS,
              meta: {
                title: () => pageTitle($t('PMS version statistics')),
                requiredCrmModule: CrmModule.SYSTEM_PMS_VERSION_OVERRIDES,
              },
              component: () =>
                import('~/pages/system/pms-version-overrides/pages/statistics/Index.vue'),
            },
          ],
        },
      ],
    },

    {
      path: '/p1-crm',
      meta: { layout: Layouts.NO_AUTH },
      children: P1routes,
    },

    {
      path: '/:pathMatch(.*)*',
      redirect: () => ({ name: Routes.HOME_INDEX }),
    },
  ],
})

const pageTitle = (moduleTitle: string): string => {
  return `CRM - ${moduleTitle}`
}

router.beforeResolve(moduleGuard)

router.afterEach((to) => {
  if (to.meta.title) {
    document.title = to.meta.title()
  }
})

router.onError((error) => {
  if (!isChunkLoadError(error?.message || '')) {
    return
  }

  recoverFromChunkLoadError()
})

export default router

declare module 'vue-router' {
  interface RouteMeta {
    layout?: Layouts
    title?: () => string
    requiredCrmModule?: CrmModule
  }
}
